import React, { useState, useRef, useEffect, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { FaCheckCircle } from "react-icons/fa";
import { RxChevronDown, RxChevronUp } from "react-icons/rx";
import LearningComponentBot from "../learning/LearningComponentBot";
import SkeletonCard from "../skeletons/SkeletonCard";
import moment from "moment";
import { formatCurrency, formatMinutes, getRandomColor } from "../../utils";

// const pieData = [
//   { name: "BTC", target: 72, actual: 73, color: "#FFA041" },
//   { name: "ETH", target: 17, actual: 18, color: "#56A5FF" },
//   { name: "XRP", target: 6, actual: 4.6, color: "#585858" },
//   { name: "XRP", target: 4, actual: 3.9, color: "#C9A1E0" },
//   { name: "SHIB", target: 1, actual: 0.5, color: "#FF9191" },
// ];

const BotOverviewRebalance = ({ selectedBot, loading, setOpenDialog }) => {
  // const netProfit =
  //   (selectedBot?.realized_pnl || 0) + (selectedBot?.unrealized_pnl || 0);

  // // This ROI calculation is now guaranteed to be consistent with the P/L figures.
  // const roi =
  //   selectedBot?.capital > 0 ? (netProfit / selectedBot?.capital) * 100 : 0;

  // // These percentage calculations remain correct.
  // const realizedPercentage =
  //   selectedBot?.capital > 0
  //     ? (selectedBot?.realized_pnl / selectedBot?.capital) * 100
  //     : 0;

  // const unrealizedPercentage =
  //   selectedBot?.capital > 0
  //     ? (selectedBot?.unrealized_pnl / selectedBot?.capital) * 100
  //     : 0;
  
  // 3
  // Get capital and current value safely
  const capital = selectedBot?.capital || 0;
  const currentValue = selectedBot?.current_balance || 0;

  // Calculate net ROI using backend-provided current value
  const netProfit = currentValue - capital;
  const roi = capital > 0 ? (netProfit / capital) * 100 : 0;

  // Realized and unrealized P/L remain displayed separately
  const realizedPnL = selectedBot?.realized_pnl || 0;
  const unrealizedPnL = selectedBot?.unrealized_pnl || 0;

  // Calculate percentage contributions
  const realizedPercentage = capital > 0 ? (realizedPnL / capital) * 100 : 0;
  const unrealizedPercentage =
    capital > 0 ? (unrealizedPnL / capital) * 100 : 0;

  const start = moment(selectedBot?.created_at);

  // Format date like "21 Nov 2024, 19:20 (UTC+8)"
  const formattedDate =
    start.format("DD MMM YYYY, HH:mm") + ` (${start.format("z") || "UTC"})`;

  // Calculate duration from start until now
  const now = moment();
  const duration = moment.duration(now.diff(start)); // difference between now and start

  const days = Math.floor(duration.asDays()); // full days active
  const hours = duration.hours();

  const pieData = useMemo(() => {
    return (selectedBot?.setting?.allocations || []).map((item) => ({
      name: item.coin_symbol,
      target: item.allocation_percentage,
      actual: item.allocation_percentage,
      color: `#${Math.floor(Math.random() * 0xffffff)
        .toString(16)
        .padStart(6, "0")}`,
    }));
  }, [selectedBot?.setting?.allocations]);

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <div className="p-4 bg-[#D3C4FF]/10 text-[#1F2937] text-sm rounded-xl">
        <div className="flex flex-col gap-4 w-full">
          <div className="">
            <div className="space-y-4">
              <div className="flex items-center gap-2 w-full">
                <div className="inline-block">
                  <img
                    src="images/icons/icon_bot_rebalance.svg"
                    alt="bot icon"
                    className="w-6 h-6 rounded-full"
                  />
                  <img
                    src="/binance-icon.png"
                    alt="binance"
                    className="w-6 h-6 rounded-full absolute top-0 right-0"
                  />
                </div>
                <p className="text-[#999999] leading-none text-[16px]">
                  Bot Active since
                </p>

                <p className="text-[#999999] leading-none text-[16px]">
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["100px"]}
                      heights={["10px"]}
                    />
                  ) : (
                    formattedDate
                  )}
                </p>
                <p className="text-[#999999] leading-none text-[16px]">
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["10px"]}
                    />
                  ) : (
                    <>
                      {days}d {hours}h
                    </>
                  )}
                </p>

                <p className="text-[#999999] leading-none text-[16px]">
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["40px"]}
                      heights={["8px"]}
                    />
                  ) : (
                    selectedBot?.bot_status
                  )}
                </p>
                <p className="text-[#999999] leading-none text-[16px]">
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["80px"]}
                      heights={["8px"]}
                    />
                  ) : (
                    selectedBot?.coin_name
                  )}
                </p>
                <p className="flex items-center gap-1 text-xs text-gray-400">
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["20px"]}
                      heights={["8px"]}
                    />
                  ) : (
                    selectedBot?.strategy_name
                  )}
                  &nbsp;•&nbsp;
                  {loading ? (
                    <SkeletonCard
                      type="text"
                      lines={1}
                      widths={["20px"]}
                      heights={["8px"]}
                    />
                  ) : (
                    <span className="capitalize">
                      {selectedBot?.exchange_name}
                    </span>
                  )}
                </p>

                {/* ELIPSIS VERTICAL */}
                {/* Ikon Elipsis mepet kanan */}
                <svg
                  onClick={() => setOpen((prev) => !prev)}
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 cursor-pointer ml-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v.01M12 12v.01M12 18v.01"
                  />
                </svg>

                {/* Modal */}
                {open && (
                  <div
                    ref={dropdownRef}
                    className="absolute w-52 right-5 top-8 rounded-lg shadow-lg bg-white  z-10"
                  >
                    <ul className="text-sm text-gray-700 p-2">
                      <li>
                        <button className="w-full text-left bg-white hover:bg-gray-100 font-medium text-black">
                          View Trade History
                        </button>
                      </li>
                      <li>
                        <button className="w-full text-left bg-white hover:bg-gray-100 font-medium text-black">
                          View Bot Log
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              <div
                className="p-4 bg-[#F9FAFB] text-[#1F2937] text-sm rounded-xl"
                style={{ border: "1px solid #dde1e6" }}
              >
                <p className="font-bold text-base">Investment</p>
                <div className="flex justify-between">
                  <div>
                    <p className="text-xs leading-none">Starting Value</p>
                    <p className="flex items-center gap-2 font-bold text-base leading-none">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["40px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(selectedBot?.capital || 0)
                      )}{" "}
                      USDT
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-right leading-none">
                      Current Value
                    </p>
                    <p className="flex items-center gap-2 font-bold text-base text-right leading-none">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["40px"]}
                          heights={["10px"]}
                        />
                      ) : (
                        formatCurrency(selectedBot?.current_balance || 0)
                      )}{" "}
                      USDT
                    </p>
                  </div>
                </div>
                {/* ROI */}
                <div className="flex items-center justify-between">
                  <p className="font-bold text-base leading-none">ROI</p>
                  <p className="flex items-center gap-2 font-bold text-base text-right leading-none">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      <>
                        {netProfit >= 0 ? "+" : "-"}
                        {formatCurrency(netProfit || 0)}
                      </>
                    )}{" "}
                    USDT
                  </p>
                </div>
                {!loading && (
                  <div className="text-right mb-3 leading-none">
                    <span
                      className={`px-3 py-[3px] pb-1.5 text-[11px] ${
                        roi >= 0 ? "bg-green-500" : "bg-red-500"
                      }  text-white rounded-xl text-center whitespace-nowrap w-fit`}
                    >
                      {roi >= 0 ? "+" : "-"}({formatCurrency(roi || 0)}%)
                    </span>
                  </div>
                )}
                {/* Realized & Unrealized */}
                <div className="flex justify-between text-sm">
                  {/* Realized */}
                  <div>
                    <p className="text-sm text-gray-500 leading-none">
                      Realized P/L
                    </p>
                    <p className="font-medium text-sm text-gray-800 leading-none">
                      {loading ? (
                        <SkeletonCard
                          type="text"
                          lines={1}
                          widths={["40px"]}
                          heights={["8px"]}
                        />
                      ) : (
                        <>
                          {selectedBot?.realized_pnl >= 0 ? "+" : "-"}
                          {formatCurrency(selectedBot?.realized_pnl || 0)} USDT
                        </>
                      )}
                    </p>
                    {!loading && (
                      <p
                        className={`text-sm ${
                          realizedPercentage >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }  font-medium leading-none`}
                      >
                        {realizedPercentage >= 0 ? "+" : "-"}
                        {formatCurrency(realizedPercentage || 0)}%
                      </p>
                    )}
                  </div>

                  {/* Unrealized */}
                  <div className="text-right">
                    <p className="text-sm text-gray-500 leading-none">
                      Unrealized P/L
                    </p>
                    <p className="font-medium text-sm text-gray-800 leading-none">
                      {loading ? (
                        <div className="w-full flex items-end justify-end">
                          <SkeletonCard
                            type="text"
                            lines={1}
                            widths={["40px"]}
                            heights={["8px"]}
                          />
                        </div>
                      ) : (
                        <>
                          {selectedBot?.unrealized_pnl >= 0 ? "+" : "-"}
                          {formatCurrency(
                            selectedBot?.unrealized_pnl || 0
                          )}{" "}
                          USDT
                        </>
                      )}
                    </p>
                    {!loading && (
                      <p
                        className={`text-sm ${
                          unrealizedPercentage >= 0
                            ? "text-green-600"
                            : "text-red-600"
                        }  font-medium leading-none`}
                      >
                        {unrealizedPercentage >= 0 ? "+" : "-"}
                        {formatCurrency(unrealizedPercentage || 0)}%
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>{" "}
            <div className="flex gap-2 mt-4 justify-end">
              <a className="bg-transparent hover:bg-[#374151] hover:text-white ring-1 ring-black text-black px-5 py-2 text-center rounded-lg cursor-pointer">
                Edit Settings
              </a>
              <a
                className="bg-[#FF3B30] hover:bg-red-700 text-white px-5 py-2 text-center rounded-lg cursor-pointer"
                onClick={() => setOpenDialog("stop-bot")}
              >
                Stop Bot
              </a>
            </div>
          </div>
        </div>{" "}
        <div className="">
          <div className="bg-white rounded-xl p-4 p-4">
            <p className="font-bold text-base mb-4">Allocation Asset</p>
            <div className="flex items-center ">
              {/* Pie Chart */}
              <div className="relative h-[220px] w-[220px]">
                <PieChart width={220} height={220}>
                  <Pie
                    data={pieData}
                    dataKey="target"
                    outerRadius={80}
                    innerRadius={55}
                    paddingAngle={2}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        style={{ fill: entry.color }}
                      />
                    ))}
                  </Pie>
                </PieChart>
                {/* Center Label */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                  <p className="text-xs text-gray-500">Asset</p>
                  <p className="font-bold text-lg">
                    {formatCurrency(selectedBot?.setting?.capital || 0)}
                  </p>
                  <p className="text-xs font-semibold text-gray-700">USDT</p>
                </div>
              </div>

              {/* Value Boxes */}
              <div className="w-full max-w-md bg-white rounded-xl shadow p-4">
                <div className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-x-4 pb-2 border-b mb-2">
                  <div></div>
                  <p className="text-sm font-semibold text-gray-900"> </p>
                  <p className="text-sm font-semibold text-gray-900 text-right">
                    Target
                  </p>
                  <p className="text-sm font-semibold text-gray-900 text-right">
                    Actual
                  </p>
                </div>
                {loading
                  ? [...Array(3)].map((_, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-x-4"
                      >
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getRandomColor() }}
                        />
                        <p className="text-sm text-gray-900">
                          <SkeletonCard
                            type="text"
                            lines={1}
                            widths={["60px"]}
                            heights={["10px"]}
                          />
                        </p>
                        <p className="text-sm text-gray-500 text-right flex items-end justify-end">
                          <SkeletonCard
                            type="text"
                            lines={1}
                            widths={["30px"]}
                            heights={["10px"]}
                          />
                        </p>
                        <p className="text-sm text-gray-900 text-right flex items-end justify-end">
                          <SkeletonCard
                            type="text"
                            lines={1}
                            widths={["30px"]}
                            heights={["10px"]}
                          />
                        </p>
                      </div>
                    ))
                  : pieData?.length
                  ? pieData?.map((entry, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-x-4"
                      >
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: entry.color }}
                        />
                        <p className="text-sm text-gray-900">{entry.name}</p>
                        <p className="text-sm text-gray-500 text-right">
                          {entry.target}%
                        </p>
                        <p className="text-sm text-gray-900 text-right">
                          {selectedBot?.assets?.setting_actual?.[entry.name]}%
                        </p>
                      </div>
                    ))
                  : null}
              </div>
            </div>
            <div
              className="bg-white rounded-xl p-4 mt-4"
              style={{ border: "1px solid #dde1e6" }}
            >
              <div className="grid grid-cols-4 text-center text-sm text-gray-900">
                {/* Row 1 */}
                <div className="flex flex-col items-center pt-1">
                  <p className="text-xs text-gray-500 leading-none">Capital</p>
                  <hr className="w-[100px] h-[1.5px] bg-gray-400 border-0 rounded" />
                  <p className="font-semibold text-sm leading-none flex gap-2">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : (
                      formatCurrency(selectedBot?.setting?.capital || 0)
                    )}{" "}
                    USDT
                  </p>
                </div>
                <div className="flex flex-col items-center pt-1">
                  <p className="text-xs text-gray-500 leading-none">Method</p>
                  <hr className="w-[100px] h-[1.5px] bg-gray-400 border-0 rounded" />
                  <p className="font-semibold text-sm leading-none">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : selectedBot?.setting?.pattern === 0 ? (
                      "Proportionally"
                    ) : (
                      "Periodically"
                    )}
                  </p>
                </div>
                <div className="flex flex-col items-center pt-1">
                  <p className="text-xs text-gray-500 leading-none">
                    {selectedBot?.setting?.pattern === 0
                      ? "Proportion"
                      : "Periodic"}
                  </p>
                  <hr className="w-[100px] h-[1.5px] bg-gray-400 border-0 rounded" />
                  <p className="font-semibold text-sm leading-none">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : selectedBot?.setting?.pattern === 0 ? (
                      `${selectedBot?.setting?.divergence_rate}%`
                    ) : (
                      `${formatMinutes(
                        selectedBot?.setting?.time_designation || 0
                      )}m`
                    )}
                  </p>
                </div>
                <div className="flex flex-col items-center pt-1">
                  <p className="text-xs text-gray-500 leading-none">
                    Take Profit
                  </p>
                  <hr className="w-[100px] h-[1.5px] bg-gray-400 border-0 rounded" />
                  <p className="font-semibold text-sm leading-none">
                    {loading ? (
                      <SkeletonCard
                        type="text"
                        lines={1}
                        widths={["60px"]}
                        heights={["10px"]}
                      />
                    ) : selectedBot?.setting?.take_profit_status ? (
                      `
                      ${formatCurrency(
                        selectedBot?.setting?.take_profit_value || 0
                      )}
                    %`
                    ) : (
                      "No Take profit"
                    )}{" "}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotOverviewRebalance;
